import React, { useState, useEffect } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaTimes,
  FaSlidersH,
  FaLayerGroup,
  FaPlusCircle,
  FaMinusCircle,
} from "react-icons/fa";
import apiRequest from "../services/api";

const PayrollStructureManagement = () => {
  const [structures, setStructures] = useState([]);
  const [components, setComponents] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");

  const [formData, setFormData] = useState({
    id: null,
    name: "",
    status: "ACTIVE",
    items: [],
  });

  const fetchStructures = async () => {
    setIsLoading(true);
    setError("");
    try {
      const resp = await apiRequest("payrolls/structures");
      setStructures(resp.data || []);
    } catch (err) {
      setError(err?.message || "Failed to retrieve structural entries.");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchComponents = async () => {
    try {
      const resp = await apiRequest("payrolls/components");
      setComponents(resp.data || []);
    } catch (err) {
      console.error("Lookup loading failed:", err);
    }
  };

  useEffect(() => {
    fetchStructures();
    fetchComponents();
  }, []);

  const openModal = (mode, structure = null) => {
    setModalMode(mode);
    setError("");
    setSuccess("");

    if (structure) {
      setFormData({
        id: structure.id,
        name: structure.name || "",
        status: structure.status || "ACTIVE",
        items: structure.items
          ? structure.items.map((item) => ({
              component: { id: item.componentId || "" },
              amount: item.amount !== undefined ? item.amount : 0,
              _dtoName: item.name,
              _dtoType: item.type,
              _dtoCalcType: item.calculationType,
            }))
          : [],
      });
    } else {
      setFormData({
        id: null,
        name: "",
        status: "ACTIVE",
        items: [{ component: { id: "" }, amount: 0 }],
      });
    }

    setIsModalOpen(true);
  };

  const handleAddItemRow = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { component: { id: "" }, amount: 0 }],
    });
  };

  const handleRemoveItemRow = (index) => {
    const updated = [...formData.items];
    updated.splice(index, 1);
    setFormData({ ...formData, items: updated });
  };

  const handleItemChange = (index, field, value) => {
    const updated = [...formData.items];

    if (field === "componentId") {
      updated[index].component = {
        id: value ? parseInt(value, 10) : "",
      };
      delete updated[index]._dtoName;
      delete updated[index]._dtoType;
      delete updated[index]._dtoCalcType;
    } else if (field === "amount") {
      updated[index].amount = value === "" ? "" : parseFloat(value) || 0;
    }

    setFormData({ ...formData, items: updated });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    if (!formData.name.trim()) {
      setError("Salary structure is required.");
      return;
    }

    const cleanedItems = formData.items
      .filter((item) => item.component?.id)
      .map((item) => ({
        component: { id: item.component.id },
        amount: item.amount === "" ? 0 : item.amount,
      }));

    if (cleanedItems.length === 0 && modalMode !== "view") {
      setError("A structure requires at least one line component.");
      return;
    }

    const endpoint = "payrolls/structures";
    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      const resp = await apiRequest(endpoint, {
        method,
        body: {
          ...formData,
          name: formData.name.trim(),
          items: cleanedItems,
        },
      });

      setSuccess(resp.message || "Saved successfully!");
      fetchStructures();
      setIsModalOpen(false);
    } catch (err) {
      setError(err?.message || "Unable to save salary rules.");
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently remove: "${name}"?`)) return;

    setError("");

    try {
      await apiRequest(`payrolls/structures/${id}`, {
        method: "DELETE",
      });
      fetchStructures();
    } catch (err) {
      setError(err?.message || "Deletion process blocked dependencies.");
    }
  };

  const inputClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50";

  const selectClass =
    "w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition appearance-none dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50";

  return (
    <div className="min-h-full p-4 md:p-8 font-sans bg-slate-50 text-slate-800 dark:bg-slate-950 dark:text-slate-100">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white border border-slate-200 shadow-sm dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl p-6 rounded-2xl backdrop-blur-md">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white flex items-center gap-2">
              <FaSlidersH className="text-emerald-500" />
              Salary Structure
            </h1>
          </div>

          <button
            onClick={() => openModal("create")}
            className="flex items-center gap-2 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-semibold rounded-xl transition-all shadow-lg shadow-emerald-600/20"
          >
            <FaPlus /> Create
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs font-medium dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400">
            {error}
          </div>
        )}

        {/* Success */}
        {success && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-xs font-medium dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400">
            {success}
          </div>
        )}

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden shadow-sm dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-500 text-[11px] font-bold tracking-wider uppercase dark:bg-slate-950/50 dark:border-white/10 dark:text-slate-400">
                  <th className="px-6 py-4">Structure Name</th>
                  <th className="px-6 py-4">Structure Components</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100 text-slate-700 text-xs dark:divide-white/5 dark:text-slate-300">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-8 text-slate-400 dark:text-slate-500"
                    >
                      Retrieving ...
                    </td>
                  </tr>
                ) : structures.length === 0 ? (
                  <tr>
                    <td
                      colSpan="4"
                      className="text-center py-8 text-slate-400 dark:text-slate-500"
                    >
                      Not established yet.
                    </td>
                  </tr>
                ) : (
                  structures.map((struct) => (
                    <tr
                      key={struct.id}
                      className="hover:bg-slate-50 dark:hover:bg-white/[0.02] transition-colors"
                    >
                      <td className="px-6 py-4 font-semibold text-slate-900 dark:text-white text-sm">
                        {struct.name}
                      </td>

                      <td className="px-6 py-4">
                        <span className="bg-slate-100 px-2.5 py-1 rounded-md text-slate-600 font-mono text-[11px] border border-slate-200 dark:bg-slate-950/70 dark:text-slate-400 dark:border-white/10">
                          {struct.items?.length || 0} Components
                        </span>
                      </td>

                      <td className="px-6 py-4 text-center">
                        <span
                          className={`px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            struct.status === "ACTIVE"
                              ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                              : "bg-slate-100 text-slate-500 border border-slate-200 dark:bg-slate-800 dark:text-slate-500 dark:border-white/10"
                          }`}
                        >
                          {struct.status}
                        </span>
                      </td>

                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => openModal("view", struct)}
                            className="p-2 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-600 border border-slate-200 transition dark:bg-slate-800 dark:hover:bg-slate-700 dark:text-slate-300 dark:border-white/5"
                            title="View"
                          >
                            <FaEye />
                          </button>

                          <button
                            onClick={() => openModal("edit", struct)}
                            className="p-2 bg-blue-50 hover:bg-blue-100 rounded-lg text-blue-600 border border-blue-200 transition dark:bg-blue-600/10 dark:text-blue-400 dark:border-blue-500/10 dark:hover:bg-blue-600/20"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>

                          <button
                            onClick={() => handleDelete(struct.id, struct.name)}
                            className="p-2 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 border border-red-200 transition dark:bg-red-600/10 dark:text-red-400 dark:border-red-500/10 dark:hover:bg-red-600/20"
                            title="Delete"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[2000] flex flex-col justify-end">
          {/* Overlay */}
          <div
            className="absolute inset-0 bg-slate-900/40 dark:bg-slate-950/70 backdrop-blur-sm"
            onClick={() => setIsModalOpen(false)}
          />

          {/* Drawer */}
          <div className="relative w-full max-w-2xl mx-auto bg-white border border-slate-200 dark:bg-slate-900 dark:border-white/10 rounded-t-3xl p-6 sm:p-8 shadow-[0_-15px_40px_rgba(0,0,0,0.15)] dark:shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex justify-between items-start mb-6 border-b border-slate-200 dark:border-white/10 pb-4">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white capitalize flex items-center gap-2">
                  <FaLayerGroup className="text-emerald-500 text-sm" />
                  {modalMode} Salary Structure
                </h3>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 border border-slate-200 transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300 dark:border-white/5"
              >
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSave} className="space-y-6">
              {/* Structure Header Fields */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2 flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Structure Name *
                  </label>

                  <input
                    type="text"
                    required
                    disabled={modalMode === "view"}
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        name: e.target.value,
                      })
                    }
                    placeholder="e.g., Managerial Grade"
                    className={inputClass}
                  />
                </div>

                <div className="flex flex-col gap-1.5">
                  <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400">
                    Status
                  </label>

                  <select
                    disabled={modalMode === "view"}
                    value={formData.status}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        status: e.target.value,
                      })
                    }
                    className={selectClass}
                  >
                    <option value="ACTIVE">ACTIVE RUNNING</option>
                    <option value="INACTIVE">Suspended</option>
                  </select>
                </div>
              </div>

              {/* Component Lines */}
              <div className="space-y-3">
                <div className="flex justify-between items-center border-b border-slate-200 dark:border-white/10 pb-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-700 dark:text-slate-300">
                    Component lines
                  </h4>

                  {modalMode !== "view" && (
                    <button
                      type="button"
                      onClick={handleAddItemRow}
                      className="text-emerald-600 hover:text-emerald-500 dark:text-emerald-400 dark:hover:text-emerald-300 text-xs font-medium flex items-center gap-1.5"
                    >
                      <FaPlusCircle className="text-sm" /> Add
                    </button>
                  )}
                </div>

                <div className="space-y-3 max-h-64 overflow-y-auto pr-1">
                  {formData.items.map((item, idx) => {
                    const selectedComp = components.find(
                      (c) => c.id === item.component?.id
                    );

                    const displayName = selectedComp
                      ? `${selectedComp.name} [${selectedComp.type} | ${selectedComp.calculationType}]`
                      : item._dtoName
                      ? `${item._dtoName} [${item._dtoType} | ${item._dtoCalcType}]`
                      : "";

                    return (
                      <div
                        key={idx}
                        className="grid grid-cols-1 sm:grid-cols-12 gap-3 bg-slate-50 p-3 rounded-xl border border-slate-200 items-center dark:bg-slate-950/50 dark:border-white/10"
                      >
                        {/* Component */}
                        <div className="sm:col-span-7 flex flex-col gap-1">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-500 sm:hidden">
                            Component
                          </label>

                          {modalMode === "view" ? (
                            <div className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-600 dark:bg-slate-950/60 dark:border-white/10 dark:text-slate-400">
                              {displayName || "Not configured"}
                            </div>
                          ) : (
                            <select
                              required
                              value={item.component?.id || ""}
                              onChange={(e) =>
                                handleItemChange(
                                  idx,
                                  "componentId",
                                  e.target.value
                                )
                              }
                              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:focus:border-emerald-500/50"
                            >
                              <option value="" disabled hidden>
                                -- Select Component --
                              </option>

                              {components.map((comp) => (
                                <option key={comp.id} value={comp.id}>
                                  {comp.name} [{comp.type} |{" "}
                                  {comp.calculationType}]
                                </option>
                              ))}

                              {item.component?.id &&
                                !selectedComp &&
                                item._dtoName && (
                                  <option value={item.component.id}>
                                    {displayName}
                                  </option>
                                )}
                            </select>
                          )}
                        </div>

                        {/* Amount */}
                        <div className="sm:col-span-4 flex flex-col gap-1">
                          <label className="text-[9px] uppercase tracking-wider font-bold text-slate-500 sm:hidden">
                            Assign Value
                          </label>

                          <input
                            type="number"
                            step="any"
                            required
                            disabled={modalMode === "view"}
                            value={item.amount}
                            onChange={(e) =>
                              handleItemChange(idx, "amount", e.target.value)
                            }
                            placeholder="(e.g. 5000)"
                            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-800 font-mono placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 dark:bg-slate-950/60 dark:border-white/10 dark:text-white dark:placeholder-slate-600 dark:focus:border-emerald-500/50"
                          />
                        </div>

                        {/* Remove */}
                        <div className="sm:col-span-1 text-center mt-2 sm:mt-0">
                          {modalMode !== "view" && (
                            <button
                              type="button"
                              onClick={() => handleRemoveItemRow(idx)}
                              className="text-rose-500/80 hover:text-rose-600 dark:text-rose-500/80 dark:hover:text-rose-400 transition-colors"
                            >
                              <FaMinusCircle className="text-base mx-auto" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {formData.items.length === 0 && (
                    <div className="text-center py-6 text-slate-400 text-xs border border-dashed border-slate-200 rounded-xl dark:text-slate-600 dark:border-white/10">
                      No active rules.
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-white/10">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold transition dark:bg-white/5 dark:hover:bg-white/10 dark:text-slate-300"
                >
                  {modalMode === "view" ? "Dismiss" : "Cancel"}
                </button>

                {modalMode !== "view" && (
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider shadow-md shadow-emerald-600/10 transition"
                  >
                    Save Changes
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PayrollStructureManagement;
